<template lang="pug">
pr-wrapper.d-flex(fs, bg, :style="themeStyle")
  .feedback.w-100.h-100.d-flex.flex-column.align-items-center
    .text-center.py-4
      pr-text(weight="bold", size="large") {{ $t("Feedback") }}

    .feedback-scroll-container(ref="scrollContainer", v-if="!blockedMessage")
      .feedback-content.px-5(
        v-nv:scope="'feedback'",
        data-scroll="center",
        nv-sleep-protect,
        nv-exit-protect,
        @nv-exit="close",
        @nv-back="close",
        @nv-down="scrollDown",
        @nv-up="scrollUp"
      )
        //- Questions
        .questions-wrapper
          .question-item.mb-2(v-for="(question, questionIndex) in questions", :key="question.id", :ref="`question-${questionIndex}`")
            pr-text.question-title.mb-2(weight="semibold", size="medium") {{ question.question }}
            .choice-options
              .choice-item(
                v-for="(option, index) in question.options || []",
                :key="`${question.id}-${index}`",
                :id="`feedback-q-${questionIndex}-o-${index}`",
                @nv-enter="selectChoice(question.id, option, questionIndex)",
                v-nv
              )
                pr-text.background(size="medium", :class="{ 'inactive-element': answers[question.id] === option }") {{ option }}


        //- Submit Button
        .submit-section.text-center.my-2
          .submit-button(@nv-enter="submit", v-nv)
            pr-text.background(size="medium", weight="semibold") {{ $t("Submit Feedback") }}

    .blocked-message.d-flex.flex-column.align-items-center.justify-content-center.flex-grow-1(v-else, v-nv:scope="'feedback-blocked'", nv-exit-protect, nv-sleep-protect, @nv-exit="close", @nv-back="close")
      pr-text(size="medium") {{ blockedMessage }}
      .close-button.mt-4(@nv-enter="close", v-nv)
        pr-text.background(size="medium") {{ $t("Close") }}

</template>

<script>
import { mapGetters, mapState } from "vuex"

export default {
  name: "feedback",
  data() {
    return {
      questions: [],
      rawQuestions: [],
      answers: {},
      blockedMessage: null
    }
  },
  computed: {
    ...mapState(["guest", "theme", "device"]),
    ...mapGetters(["language"]),
    themeStyle() {
      return {
        "--feedback-border": this.theme.color.border,
        "--feedback-border-active": this.theme.color.onSurface,
        "--feedback-primary": this.theme.color.primary
      }
    }
  },
  async activated() {
    this.blockedMessage = null

    if (!this.guest) {
      this.blockedMessage = this.$t("It cannot be done because there are no guests.")
      await this.$nextTick()
      _$.nav.sync("feedback-blocked", true)
      return
    }

    if (this.guest.feedback === "1") {
      this.blockedMessage = this.$t("Your feedback has been submitted. Thank you!")
      await this.$nextTick()
      _$.nav.sync("feedback-blocked", true)
      return
    }

    await this.loadQuestions()
    await this.$nextTick()
    _$.nav.sync("feedback", true)
  },
  methods: {
    async loadQuestions() {
      const data = await __.api.get.feedbackQuestions()
      if (!Array.isArray(data)) return

      const defaultLocale = __.device.config("default_language./lang/") || "en_US"
      const locale = this.language?.code || defaultLocale

      this.rawQuestions = data
      this.questions = data.map(q => ({
        ...q,
        question: (q.question[locale] || q.question[defaultLocale]) ?? q.question,
        options: (q.options[locale] || q.options[defaultLocale]) ?? q.options
      }))
    },
    close() {
      __.platform.go()
    },
    selectChoice(questionId, option, questionIndex) {
      this.$set(this.answers, questionId, option)
      this.goToNextQuestion(questionIndex)
    },
    async submit() {
      const unanswered = this.questions.filter(q => !this.answers[q.id])
      if (unanswered.length > 0) {
        _$.vue.$toast({ element: "top-end", content: this.$t("Please answer all questions."), theme: "danger" })
        return
      }

      const defaultLocale = __.device.config("default_language./lang/") || "en_US"
      const locale = this.language?.code || defaultLocale
      const payload = {
        guest_id: this.guest.id,
        guest_name: this.guest.name,
        guest_surname: this.guest.surname,
        room: this.device.location,
        answers: this.rawQuestions.map(q => {
          const selectedAnswer = this.answers[q.id] || null
          const translatedOptions = q.options[locale] || q.options[defaultLocale]
          const defaultOptions = q.options[defaultLocale]
          const answerIndex = translatedOptions?.indexOf(selectedAnswer)
          return {
            id: q.id,
            question: q.question[defaultLocale] ?? q.question,
            answer: answerIndex !== -1 ? defaultOptions?.[answerIndex] : selectedAnswer
          }
        })
      }
      const result = await __.api.set.feedback(payload)
      if (result) {
        _$.vue.$toast({ element: "top-end", content: this.$t("Your feedback has been submitted. Thank you!"), theme: "success" })
        this.guest.feedback = "1"
        __.platform.go()
      } else {
        _$.vue.$toast({
          element: "top-end",
          content: this.$t("An error occurred while submitting your feedback. Please try again later."),
          theme: "danger"
        })
      }
    },
    scrollDown() {
      let scrollTop = this.$refs.scrollContainer.scrollTop
      scrollTop += 100
      $(this.$refs.scrollContainer).animate({ scrollTop: scrollTop }, 100)
    },
    scrollUp() {
      let scrollTop = this.$refs.scrollContainer.scrollTop
      scrollTop -= 100
      $(this.$refs.scrollContainer).animate({ scrollTop: scrollTop }, 100)
    },
    goToNextQuestion(questionIndex) {
      const nextIndex = questionIndex + 1
      const nextQuestionRef = this.$refs[`question-${nextIndex}`]

      if (!nextQuestionRef) {
        const submitButton = this.$el.querySelector(".submit-button")
        if (submitButton) {
          const scrollContainer = this.$refs.scrollContainer
          $(scrollContainer).animate({ scrollTop: scrollContainer.scrollHeight }, 200, () => {
            _$.nav.move(submitButton)
          })
        }
        return
      }

      const target = Array.isArray(nextQuestionRef) ? nextQuestionRef[0] : nextQuestionRef
      if (!target) return
      const nextOption = document.getElementById(`feedback-q-${nextIndex}-o-0`)

      let scrollTop = this.$refs.scrollContainer.scrollTop
      scrollTop += 100
      $(this.$refs.scrollContainer).animate({ scrollTop: scrollTop }, 100, () => {
        if (nextOption) _$.nav.move(nextOption)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.feedback {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  overflow: hidden;

  .blocked-message {
    text-align: center;

    .close-button {
      .background {
        padding: 15px 40px;
        border: 2px solid var(--feedback-border);
        border-radius: 10px;
        text-align: center;
      }

      &.nv-el-current {
        .background {
          border-color: var(--feedback-border-active);
        }
      }
    }
  }

  .feedback-scroll-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }

    .feedback-content {
      max-width: 1000px;
      margin: 0 auto;
      padding-bottom: 20px;

      .questions-wrapper {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .question-item {
          .question-title {
            text-align: center;
            display: block;
          }

          .choice-options {
            display: flex;
            justify-content: center;
            // flex-wrap: wrap;
            gap: 15px;
            max-width: 100%;
            margin: 0 auto;

            .choice-item {
              .background {
                padding: 20px 30px;
                border: 2px solid var(--feedback-border);
                border-radius: 10px;
                text-align: center;
                white-space: nowrap;
                margin-right: 10px;
              }

              &.nv-el-current {
                .background {
                  border-color: var(--feedback-border-active);
                }
              }

              .inactive-element {
                border-color: var(--feedback-primary) !important;
              }
            }
          }
        }
      }

      .submit-section {
        margin-top: 40px;
        padding: 20px;

        .submit-button {
          display: inline-block;

          .background {
            padding: 20px 60px;
            border: 2px solid var(--feedback-border);
            border-radius: 10px;
            text-align: center;
            white-space: nowrap;
          }

          &.nv-el-current {
            .background {
              border-color: var(--feedback-border-active);
            }
          }
        }
      }
    }
  }
}
</style>
